/**
 * Free / Blossom-plan body for the assistant drawer.
 *
 * The launcher is visible to every couple (pro feel — no hidden features),
 * but the companion itself is Heirloom-only and the BE 403s a non-pro couple.
 * Rather than surface that as an error, a non-pro couple who opens the drawer
 * gets this teaser: what the companion does + the two upgrade paths used
 * elsewhere in the app (Upgrade → billing, and the live "try the full plan"
 * demo). Mirrors OrganizerLockedPreview's aesthetic so the paywall reads as
 * one design system.
 *
 * Copy is event-neutral (no wedding/baptism wording) — no `_baptism` variants.
 */

import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import IconifyIcon from 'components/base/IconifyIcon';
import TryFullPlanButton from 'components/sandbox/TryFullPlanButton';
import { weddingPaths } from 'routes/paths';

const FEATURES = [
  { key: 'understands', icon: 'solar:chart-2-bold-duotone' },
  { key: 'next_steps', icon: 'solar:map-arrow-right-bold-duotone' },
  { key: 'answers', icon: 'solar:chat-round-line-bold-duotone' },
] as const;

const AssistantUpsell = () => {
  const { t } = useTranslation();

  return (
    <Stack direction="column" spacing={4} sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
      <Stack direction="column" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'warning.lighter',
            color: 'warning.dark',
            border: '1px solid',
            borderColor: 'warning.light',
          }}
        >
          <IconifyIcon icon="material-symbols:auto-awesome" sx={{ fontSize: 34 }} />
        </Box>
        <Typography
          variant="overline"
          sx={{ color: 'warning.dark', fontWeight: 700, letterSpacing: 1.5, lineHeight: 1 }}
        >
          Heirloom
        </Typography>
        <Stack direction="column" spacing={1} alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {t('assistant.upsell.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380 }}>
            {t('assistant.upsell.body')}
          </Typography>
        </Stack>
      </Stack>

      <Stack direction="column" spacing={1.5} sx={{ textAlign: 'left' }}>
        {FEATURES.map((feature) => (
          <Stack key={feature.key} direction="row" spacing={1.5} alignItems="center">
            <IconifyIcon
              icon={feature.icon}
              sx={{ fontSize: 24, color: 'primary.main', flexShrink: 0 }}
            />
            <Typography variant="body2">{t(`assistant.upsell.features.${feature.key}`)}</Typography>
          </Stack>
        ))}
      </Stack>

      <Stack direction="column" spacing={1.5}>
        <Button
          component={RouterLink}
          to={weddingPaths.coupleBilling}
          variant="contained"
          color="primary"
          size="large"
          startIcon={<IconifyIcon icon="solar:crown-bold-duotone" />}
          sx={{ fontWeight: 700 }}
        >
          {t('assistant.upsell.upgrade_cta')}
        </Button>
        <TryFullPlanButton size="large" variant="soft" color="warning" sx={{ fontWeight: 700 }} />
      </Stack>
    </Stack>
  );
};

export default AssistantUpsell;
